import React from 'react';
import { ExternalLink, Download, Calendar, DollarSign, Info, Phone, Mail, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";

interface TrainingScheduleProps {
  onSectionClick: (section: string) => void;
}

const TrainingSchedule: React.FC<TrainingScheduleProps> = ({ onSectionClick }) => {
  const scrollToHeader = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="animate-fade-up training-schedule">
      {/* Training Schedule Section */}
      <div className="mb-8 sm:mb-16">
        <div className="flex items-center justify-center gap-3 mb-6 sm:mb-8">
          <Calendar className="h-5 w-5 sm:h-6 sm:w-6 text-secondary" />
          <h2 className="text-2xl sm:text-3xl font-bold text-primary">Training Schedule</h2>
        </div>
        
        <div className="bg-white shadow-lg rounded-lg p-4 sm:p-8 max-w-3xl mx-auto border border-primary/10">
          <div className="space-y-4">
            {/* Current Outdoor Location */}
            <div className="text-center border-b border-primary/10 pb-4 mb-4">
              <p className="font-medium text-black mb-1">Clanton Road Park and Arbor Glen Outreach Center</p>
              <p className="text-black">
                1520 Clanton Road<br />
                Charlotte, NC 28217
              </p>
              <p className="text-primary font-medium mt-2">Saturday Mornings: 7:00 AM - 8:00 AM</p>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">All training sessions are one hour in length</p>
            </div>
            
            {/* Future Indoor Location */}
            <div className="bg-gradient-to-r from-orange-50 to-white p-3 sm:p-4 rounded-lg border border-orange-100 mb-3">
              <div className="text-center pb-2">
                <span className="font-medium text-black">September 1, 2026</span>
                <p className="text-xs sm:text-sm text-primary font-medium mt-1">Time TBA</p>
              </div>
              <p className="text-xs sm:text-sm text-gray-700 text-center mb-1">
                <span className="font-medium">The Bleachery Field House</span><br />
                605 West White Street<br />
                Rock Hill, SC 29730
              </p>
              <p className="text-xs sm:text-sm text-primary font-medium text-center mt-2">
                1 Hour Sessions Only
              </p>
              <div className="mt-3 flex justify-center">
                <a href="#training-costs" className="w-full max-w-xs">
                  <Button variant="outline" className="w-full flex items-center justify-center gap-2 bg-white hover:bg-primary/5 text-xs sm:text-sm py-2">
                    <DollarSign size={16} />
                    <span>View Pricing</span>
                  </Button>
                </a>
              </div>
            </div>
            
            {/* What to Bring Section */}
            <div className="bg-gradient-to-r from-blue-50 to-white p-3 sm:p-4 rounded-lg border border-blue-100 mb-3">
              <div className="flex items-center gap-2 mb-2">
                <Info className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 flex-shrink-0" />
                <h4 className="font-medium text-blue-600">What to Bring</h4>
              </div>
              <p className="text-xs sm:text-sm text-gray-700">
                Please bring your own basketball and jump rope to each session. These items are essential for the training exercises and help ensure you get the most out of each workout.
              </p>
            </div>
            
            {/* Walk-ins Welcome Information */}
            <div className="mt-4 p-3 sm:p-4 bg-primary/5 rounded-lg border border-primary/20">
              <p className="text-black text-center font-medium">
                Walk-ins Are Welcome!
              </p>
              <p className="text-xs sm:text-sm text-gray-700 text-center">
                Just arrive 15 minutes early to complete registration. Feel free to download and complete prior to arrival for a faster check-in process.
              </p>
              
              {/* Late Entry Policy */}
              <p className="text-xs sm:text-sm text-red-600 text-center mt-2 font-medium">
                Please note: We don't allow entry past 15 minutes after the start time.
              </p>
              
              {/* Download Registration Form Button */}
              <div className="mt-4 flex justify-center">
                <a 
                  href="https://drive.google.com/file/d/1lBQDAONiSZsdG0N2_6jeStaByF1wYhWj/view?usp=drive_link"
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-full max-w-xs"
                >
                  <Button variant="outline" className="w-full flex items-center justify-center gap-1 sm:gap-2 bg-white hover:bg-primary/5 text-xs sm:text-sm py-2">
                    <Download size={16} />
                    <span>Download Registration Form</span>
                    <ExternalLink size={14} />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Chicago Bulls Image Section */}
      <div className="mb-8 sm:mb-16">
        <div className="max-w-lg mx-auto">
          <div className="bg-white shadow-lg rounded-lg p-4 sm:p-6 border border-primary/10">
            <div className="rounded-lg overflow-hidden mb-4">
              <img 
                src="/lovable-uploads/63401d98-8353-40c5-9369-518e887bc119.png" 
                alt="Dr. Butler and Patrick Williams, Chicago Bulls" 
                className="w-full h-auto object-cover"
              />
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-lg text-primary mb-1">
                Dr. Butler and Patrick Williams
              </h3>
              <p className="text-sm text-gray-600">
                Chicago Bulls (West Charlotte Class of 2019)
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Training Costs Section */}
      <div id="training-costs" className="mb-8 sm:mb-16 scroll-mt-24">
        <div className="flex items-center justify-center gap-3 mb-6 sm:mb-8">
          <DollarSign className="h-5 w-5 sm:h-6 sm:w-6 text-secondary" />
          <h2 className="text-2xl sm:text-3xl font-bold text-primary">Training Costs</h2>
        </div>
        
        <div className="bg-white shadow-lg rounded-lg p-4 sm:p-8 max-w-3xl mx-auto border border-primary/10">
          {/* Rate Increase Notice */}
          <div className="mb-6 p-3 sm:p-4 bg-gradient-to-r from-orange-50 to-white rounded-lg border border-orange-200">
            <div className="flex items-start gap-2">
              <Info className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-orange-700 text-sm sm:text-base">Rate Increase Effective September 1, 2026</h4>
                <p className="text-xs sm:text-sm text-gray-700 mt-1">
                  Our first rate increase in more than 20 years: training rates will move from $25 per hour to $50 per hour. Thank you for your continued support.
                </p>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 gap-4">
            <div className="bg-gradient-to-r from-purple-50 to-white p-3 sm:p-4 rounded-lg border border-primary/10 transition-all hover:shadow-md">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
                <div>
                  <h4 className="font-medium text-primary">Guard Training</h4>
                  <p className="text-xs sm:text-sm text-gray-600 text-left">Ages 7-17</p>
                </div>
                <div className="mt-2 sm:mt-0 bg-primary/10 px-2 sm:px-3 py-1 rounded-full text-primary font-medium text-xs sm:text-sm">
                  $50 per hour
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-purple-50 to-white p-3 sm:p-4 rounded-lg border border-primary/10 transition-all hover:shadow-md">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
                <div>
                  <h4 className="font-medium text-primary">Forward/Center Training</h4>
                  <p className="text-xs sm:text-sm text-gray-600 text-left">Ages 14-17</p>
                </div>
                <div className="mt-2 sm:mt-0 bg-primary/10 px-2 sm:px-3 py-1 rounded-full text-primary font-medium text-xs sm:text-sm">
                  $50 per hour
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-purple-50 to-white p-3 sm:p-4 rounded-lg border border-primary/10 transition-all hover:shadow-md">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
                <div>
                  <h4 className="font-medium text-primary">Adult Training</h4>
                  <p className="text-xs sm:text-sm text-gray-600 text-left">Ages 18 & up</p>
                </div>
                <div className="mt-2 sm:mt-0 bg-primary/10 px-2 sm:px-3 py-1 rounded-full text-primary font-medium text-xs sm:text-sm">
                  $100 per hour
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-purple-50 to-white p-3 sm:p-4 rounded-lg border border-primary/10 transition-all hover:shadow-md">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
                <div>
                  <h4 className="font-medium text-primary">Minor League Pros</h4>
                </div>
                <div className="mt-2 sm:mt-0 bg-primary/10 px-2 sm:px-3 py-1 rounded-full text-primary font-medium text-xs sm:text-sm">
                  $100 per hour
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-purple-50 to-white p-3 sm:p-4 rounded-lg border border-primary/10 transition-all hover:shadow-md">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
                <div>
                  <h4 className="font-medium text-primary">Professional Players</h4>
                </div>
                <div className="mt-2 sm:mt-0 bg-primary/10 px-2 sm:px-3 py-1 rounded-full text-primary font-medium text-xs sm:text-sm">
                  $150 per hour
                </div>
              </div>
            </div>
            
            {/* Parent Policy Disclaimer */}
            <div className="bg-gradient-to-r from-red-50 to-white p-3 sm:p-4 rounded-lg border border-red-100">
              <div className="flex items-center gap-2 mb-2">
                <Info className="h-4 w-4 sm:h-5 sm:w-5 text-red-600 flex-shrink-0" />
                <h4 className="font-medium text-red-600">Important Note for Parents</h4>
              </div>
              <p className="text-xs sm:text-sm text-gray-700 font-medium">
                Parents are not allowed in the gym during training times. This policy helps maintain focus and eliminates distractions for all players.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Training Packages Section */}
      <div className="mb-8 sm:mb-16">
        <div className="flex items-center justify-center gap-3 mb-6 sm:mb-8">
          <Calendar className="h-5 w-5 sm:h-6 sm:w-6 text-secondary" />
          <h2 className="text-2xl sm:text-3xl font-bold text-primary">Training Packages</h2>
        </div>
        
        <div className="bg-white shadow-lg rounded-lg p-4 sm:p-8 max-w-3xl mx-auto border border-primary/10">
          <p className="text-center text-gray-700 mb-6 text-sm sm:text-base font-medium">
            Investment in Your Child — One Hour Each Session
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-gradient-to-b from-purple-50 to-white p-4 sm:p-6 rounded-lg border border-primary/10 text-center transition-all hover:shadow-md">
              <div className="bg-primary/10 w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-primary font-bold text-lg">A</span>
              </div>
              <h4 className="font-semibold text-primary text-lg mb-1">Three Months</h4>
              <p className="text-gray-600 text-sm">12 Weeks</p>
              <p className="text-primary font-bold text-lg mt-2">$1,200</p>
            </div>
            
            <div className="bg-gradient-to-b from-purple-50 to-white p-4 sm:p-6 rounded-lg border border-primary/10 text-center transition-all hover:shadow-md">
              <div className="bg-primary/10 w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-primary font-bold text-lg">B</span>
              </div>
              <h4 className="font-semibold text-primary text-lg mb-1">Six Months</h4>
              <p className="text-gray-600 text-sm">25 Weeks</p>
              <p className="text-primary font-bold text-lg mt-2">$2,400</p>
            </div>
            
            <div className="bg-gradient-to-b from-purple-50 to-white p-4 sm:p-6 rounded-lg border border-primary/10 text-center transition-all hover:shadow-md">
              <div className="bg-primary/10 w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-primary font-bold text-lg">C</span>
              </div>
              <h4 className="font-semibold text-primary text-lg mb-1">One Year</h4>
              <p className="text-gray-600 text-sm">52 Weeks</p>
              <p className="text-primary font-bold text-lg mt-2">$4,800</p>
            </div>
          </div>
          
          <div className="mt-6 p-3 sm:p-4 bg-gradient-to-r from-orange-50 to-white rounded-lg border border-orange-100">
            <div className="flex items-center gap-2 justify-center">
              <DollarSign className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600 flex-shrink-0" />
              <p className="text-sm sm:text-base text-gray-700 font-semibold text-center">
                All Fee Payments Are Cash Only
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Quick Links */}
      <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mt-6 sm:mt-8">
        <Button 
          variant="outline"
          onClick={() => onSectionClick("testimonials")}
          className="bg-white hover:bg-primary/5 text-xs sm:text-sm py-2"
        >
          View Testimonials
        </Button>
        <Button 
          variant="outline"
          onClick={() => onSectionClick("training-videos")}
          className="bg-white hover:bg-primary/5 text-xs sm:text-sm py-2"
        >
          Training Videos
        </Button>
        <a 
          href="https://www.all-starbasketball.com/" 
          target="_blank" 
          rel="noopener noreferrer"
        >
          <Button variant="outline" className="bg-white hover:bg-primary/5 text-xs sm:text-sm py-2">
            Our Credentials <ExternalLink className="ml-1 h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
        </a>
      </div>
    </div>
  );
};

export default TrainingSchedule;
